import { useRiskFilters } from '@app/modules/Risk/shared/hooks/useRiskFilters'
import type { RiskWorkspace } from '@app/types'
import { useSearchParams } from 'react-router-dom'
import { RiskProfileMetricOption } from '../types/riskProfile.types'
import { DamageState } from '../types/riskProfileSelected.types'
import { useSelectedWorkspaceFromUrl } from './useSelectedWorkspaceFromUrl'

export const METRIC_OPTIONS: RiskProfileMetricOption[] = [
  { value: 'damage_cost', metric: 'damage_cost', type: 'total', label: 'Damage cost' },
  { value: 'damage_cost_pc', metric: 'damage_cost', type: 'percentage', label: 'Damage cost (%)' },
  {
    value: 'exposed_value',
    metric: 'replacement_cost',
    type: 'total',
    label: 'Exposed value',
  },
  { value: 'damage_severity', metric: 'damage_severity', type: 'total', label: 'Damage severity' },
  { value: 'quantity', metric: 'quantity', type: 'total', label: 'Quantity' },
  { value: 'quantity_pc', metric: 'quantity', type: 'percentage', label: 'Quantity (%)' },
] as const

export interface ProfileAnalysisFilters {
  metricOption: RiskProfileMetricOption
  areaGroup: string
  areas: string[]
  selectedHazardGroups: string[]
  selectedElementGroups: string[]
  damageStates: DamageState[]
  selectedWorkspace: RiskWorkspace | null
  selectedAreaTypesLabel: string
  setMetricOption: (value: string) => void
}
/**
 * Reads and derives all URL-based filter state for the Profile Analysis page.
 *
 * areaGroup/areas/hazardGroups/elementGroups delegate to useRiskFilters, the
 * shared Risk-module selection source of truth, so Profile stays in sync with
 * Map/Data/Story. metricOption stays local — it's a Profile-specific concept
 * (total vs. percentage variants) not shared with other submodules.
 */
export const useProfileAnalysisFilters = (): ProfileAnalysisFilters => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedWorkspace } = useSelectedWorkspaceFromUrl()
  const { areaGroup, areas, hazardGroups, elementGroups, damageStates } = useRiskFilters()

  const metricParam = searchParams.get('metric')
  const metricOption =
    METRIC_OPTIONS.find((option) => option.value === metricParam) || METRIC_OPTIONS[0]

  const stripCountPrefix = (value: string) => value.replace(/^\d+\s+/, '')

  const selectedAreaTypesLabel =
    areas.length > 0 && areas.length <= 3 ? areas.join(', ') : stripCountPrefix(areaGroup ?? '')

  const setMetricOption = (nextMetric: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('metric', nextMetric)
    setSearchParams(params)
  }

  return {
    metricOption,
    areaGroup: areaGroup ?? '',
    areas,
    selectedHazardGroups: hazardGroups,
    selectedElementGroups: elementGroups,
    damageStates,
    selectedWorkspace,
    selectedAreaTypesLabel,
    setMetricOption,
  }
}
